import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import {
  Upload, Link as LinkIcon, Trash2, Play, FileText,
  Presentation, Plus, Loader2, Filter, Video, ExternalLink, Youtube
} from "lucide-react";
import { logger } from "../../utils/logger";
import TrainingTeam from "./TrainingTeam";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Department-wise Training content for every staff role.
 * Admin can upload (file or link). Staff see only their department(s).
 */
const TrainingTab = ({ authAxios }) => {
  const [access, setAccess] = useState(null);          // { is_admin, allowed_departments, departments }
  const [items, setItems] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all_visible");
  const [showAddLink, setShowAddLink] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [toast, setToast] = useState("");
  const [activeVideo, setActiveVideo] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === "all_visible" ? "" : `?department=${filter}`;
      const [accessR, listR] = await Promise.all([
        authAxios.get(`${API_URL}/api/training/my-access`),
        authAxios.get(`${API_URL}/api/training/items${q}`),
      ]);
      setAccess(accessR.data);
      setItems(listR.data.items || []);
      setGrouped(listR.data.grouped || {});
    } catch (err) {
      logger.error('[Training] fetch error', err?.message);
      setToast(err?.response?.data?.detail || 'Failed to load training');
    }
    setLoading(false);
  }, [authAxios, filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const departmentLabel = useMemo(() => {
    const map = {};
    (access?.departments || []).forEach(d => { map[d.id] = d.label; });
    return map;
  }, [access]);

  const handleDelete = async (itemId) => {
    if (!window.confirm("Delete this training item?")) return;
    try {
      await authAxios.delete(`${API_URL}/api/training/items/${itemId}`);
      setToast("Deleted");
      fetchData();
    } catch (err) {
      setToast(err?.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#DC283C]" size={32} /></div>;
  }

  return (
    <div className="space-y-5" data-testid="training-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#1E1B4B] font-['Outfit']">Training Library</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {access?.is_admin
              ? `Admin — ${items.length} items across ${Object.keys(grouped).length} department(s)`
              : `Showing ${items.length} item(s) for your department(s)`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-xl px-2 py-1.5">
            <Filter size={14} className="text-gray-400" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="text-xs bg-transparent outline-none pr-2"
              data-testid="training-department-filter"
            >
              <option value="all_visible">All my departments</option>
              {(access?.departments || []).map(d => (
                <option key={d.id} value={d.id}>{d.label}</option>
              ))}
            </select>
          </div>
          {access?.is_admin && (
            <>
              <button
                onClick={() => setShowAddLink(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-gray-200 text-xs font-medium text-gray-700 hover:border-[#DC283C] hover:text-[#DC283C] transition-all"
                data-testid="training-add-link-btn"
              >
                <LinkIcon size={14} /> Add Link
              </button>
              <button
                onClick={() => setShowUpload(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-r from-[#DC283C] to-orange-500 text-white text-xs font-semibold shadow hover:shadow-md transition-all"
                data-testid="training-upload-btn"
              >
                <Upload size={14} /> Upload File
              </button>
            </>
          )}
        </div>
      </div>

      {toast && (
        <div className="px-4 py-2 rounded-lg bg-[#FFF9E6] border border-amber-200 text-xs text-amber-700 flex justify-between items-center">
          <span>{toast}</span>
          <button onClick={() => setToast("")} className="text-amber-500">×</button>
        </div>
      )}

      {/* Empty */}
      {items.length === 0 && (
        <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-12 text-center">
          <Video size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm">No training content yet for your department(s).</p>
          {access?.is_admin && (
            <p className="text-xs text-gray-400 mt-1">Click "Add Link" or "Upload File" to add your first training item.</p>
          )}
        </div>
      )}

      {/* Grid */}
      {items.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(item => (
            <TrainingCard
              key={item.item_id}
              item={item}
              deptLabel={departmentLabel[item.department] || item.department}
              isAdmin={access?.is_admin}
              onDelete={() => handleDelete(item.item_id)}
              onPlay={() => setActiveVideo(item)}
            />
          ))}
        </div>
      )}

      {/* Video / Content preview modal */}
      {activeVideo && (
        <VideoPreviewModal item={activeVideo} onClose={() => setActiveVideo(null)} authAxios={authAxios} />
      )}

      {/* Training Team workspace — inbox, assignments, progress, AI assistant */}
      <TrainingTeam authAxios={authAxios} isAdmin={access?.is_admin}/>

      {/* Add link modal */}
      {showAddLink && (
        <AddLinkModal
          departments={access?.departments || []}
          onClose={() => setShowAddLink(false)}
          onSaved={() => { setShowAddLink(false); fetchData(); setToast("Training item added"); }}
          authAxios={authAxios}
        />
      )}

      {/* Upload file modal */}
      {showUpload && (
        <UploadFileModal
          departments={access?.departments || []}
          onClose={() => setShowUpload(false)}
          onSaved={() => { setShowUpload(false); fetchData(); setToast("File uploaded"); }}
          authAxios={authAxios}
        />
      )}
    </div>
  );
};

/* ---------- Card ---------- */
const TrainingCard = ({ item, deptLabel, isAdmin, onDelete, onPlay }) => {
  const Icon = item.type === "video" ? Play : item.type === "presentation" ? Presentation : FileText;
  const isEmbeddable = ["youtube", "vimeo", "loom", "file"].includes(item.embed_type);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all group" data-testid={`training-card-${item.item_id}`}>
      {/* Thumbnail */}
      <button
        onClick={onPlay}
        className="w-full aspect-video bg-gradient-to-br from-[#1E1B4B] to-[#DC283C] flex items-center justify-center relative group"
      >
        <Icon size={48} className="text-white/90 group-hover:scale-110 transition-transform" />
        <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md bg-black/40 text-white text-[10px] uppercase tracking-wider font-semibold">
          {item.type}
        </div>
        {item.embed_type === "youtube" && (
          <Youtube size={14} className="absolute top-2 left-2 text-red-500" />
        )}
      </button>

      <div className="p-3 space-y-1.5">
        <div className="flex items-start gap-2">
          <h3 className="flex-1 text-sm font-semibold text-[#1E1B4B] line-clamp-2">{item.title}</h3>
          {isAdmin && (
            <button
              onClick={onDelete}
              className="text-gray-300 hover:text-red-500 transition"
              title="Delete"
              data-testid={`training-delete-${item.item_id}`}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
        {item.description && <p className="text-[11px] text-gray-500 line-clamp-2">{item.description}</p>}
        <div className="flex items-center justify-between pt-1.5">
          <span className="px-2 py-0.5 rounded-full bg-[#FFF0F6] text-[#DC283C] text-[10px] font-semibold">
            {deptLabel}
          </span>
          {!isEmbeddable && (
            <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-gray-400 hover:text-[#DC283C] flex items-center gap-1">
              Open <ExternalLink size={10} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------- Video / file preview modal ---------- */
const VideoPreviewModal = ({ item, onClose, authAxios }) => {
  const [fileBlob, setFileBlob] = useState(null);

  useEffect(() => {
    if (item.embed_type === "file") {
      authAxios.get(`${API_URL}/api/training/items/${item.item_id}/file`, { responseType: "blob" })
        .then((r) => setFileBlob(URL.createObjectURL(r.data)))
        .catch(err => logger.error('[Training] file load err', err?.message));
    }
    return () => { if (fileBlob) URL.revokeObjectURL(fileBlob); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.item_id, item.embed_type]);

  const embedUrl = useMemo(() => {
    const url = item.url || "";
    if (item.embed_type === "youtube") {
      const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]+)/);
      return m ? `https://www.youtube.com/embed/${m[1]}` : url;
    }
    if (item.embed_type === "vimeo") {
      const m = url.match(/vimeo\.com\/(\d+)/);
      return m ? `https://player.vimeo.com/video/${m[1]}` : url;
    }
    if (item.embed_type === "loom") {
      return url.replace("/share/", "/embed/");
    }
    if (item.embed_type === "google_drive") {
      return url.replace("/view", "/preview");
    }
    return url;
  }, [item]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="training-preview-modal">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[#1E1B4B] truncate">{item.title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="training-preview-close">×</button>
        </div>
        <div className="flex-1 bg-black">
          {item.embed_type === "file" && fileBlob ? (
            item.type === "video" ? (
              <video src={fileBlob} controls autoPlay className="w-full h-[60vh]" />
            ) : (
              <iframe src={fileBlob} title={item.title} className="w-full h-[70vh]" />
            )
          ) : item.embed_type === "file" ? (
            <div className="h-[60vh] flex items-center justify-center text-white/70">
              <Loader2 className="animate-spin mr-2" /> Loading...
            </div>
          ) : (
            <iframe
              src={embedUrl}
              title={item.title}
              className="w-full h-[60vh]"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}
        </div>
        {item.description && (
          <div className="px-4 py-3 border-t text-xs text-gray-600">{item.description}</div>
        )}
      </div>
    </div>
  );
};

/* ---------- Add link modal ---------- */
const AddLinkModal = ({ departments, onClose, onSaved, authAxios }) => {
  const [form, setForm] = useState({ title: "", description: "", department: departments[0]?.id || "all", type: "video", url: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      await authAxios.post(`${API_URL}/api/training/items`, form);
      onSaved();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="training-add-link-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3">
        <h3 className="text-lg font-bold text-[#1E1B4B] flex items-center gap-2"><Plus size={18}/> Add Training Link</h3>
        <input required placeholder="Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="training-link-title" />
        <textarea placeholder="Description (optional)" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm" rows={2} />
        <div className="grid grid-cols-2 gap-2">
          <select value={form.department} onChange={e => setForm({...form, department: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="training-link-dept">
            {departments.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
          </select>
          <select value={form.type} onChange={e => setForm({...form, type: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm">
            <option value="video">Video</option>
            <option value="link">Link</option>
            <option value="document">Document</option>
          </select>
        </div>
        <input required type="url" placeholder="https://youtube.com/… or any URL" value={form.url} onChange={e => setForm({...form, url: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="training-link-url" />
        {err && <p className="text-xs text-red-500">{err}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs rounded-lg border">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 text-xs rounded-lg bg-gradient-to-r from-[#DC283C] to-orange-500 text-white font-semibold disabled:opacity-50" data-testid="training-link-submit">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};

/* ---------- Upload file modal ---------- */
const UploadFileModal = ({ departments, onClose, onSaved, authAxios }) => {
  const [form, setForm] = useState({ title: "", description: "", department: departments[0]?.id || "all", content_type: "video" });
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setErr("Select a file"); return; }
    setSaving(true); setErr(""); setProgress(0);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("department", form.department);
      fd.append("content_type", form.content_type);
      fd.append("file", file);
      await authAxios.post(`${API_URL}/api/training/items/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => setProgress(Math.round((evt.loaded * 100) / (evt.total || 1))),
      });
      onSaved();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Upload failed");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="training-upload-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3">
        <h3 className="text-lg font-bold text-[#1E1B4B] flex items-center gap-2"><Upload size={18}/> Upload Training File</h3>
        <input required placeholder="Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="training-upload-title" />
        <textarea placeholder="Description (optional)" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm" rows={2} />
        <div className="grid grid-cols-2 gap-2">
          <select value={form.department} onChange={e => setForm({...form, department: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="training-upload-dept">
            {departments.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
          </select>
          <select value={form.content_type} onChange={e => setForm({...form, content_type: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm">
            <option value="video">Video</option>
            <option value="document">Document</option>
            <option value="presentation">Presentation</option>
          </select>
        </div>
        <input required type="file" onChange={e => setFile(e.target.files[0])} className="w-full px-3 py-2 border rounded-lg text-xs" accept=".mp4,.mov,.webm,.pdf,.ppt,.pptx,.doc,.docx,.jpg,.jpeg,.png" data-testid="training-upload-file" />
        {saving && (
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#DC283C] to-orange-500" style={{ width: `${progress}%` }} />
          </div>
        )}
        {err && <p className="text-xs text-red-500">{err}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs rounded-lg border">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 text-xs rounded-lg bg-gradient-to-r from-[#DC283C] to-orange-500 text-white font-semibold disabled:opacity-50" data-testid="training-upload-submit">
            {saving ? `Uploading… ${progress}%` : "Upload"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TrainingTab;
